<template>
  <div class="attached-toolbar" :class="cssClasses">
    <slot />
    <cv-tooltip v-if="showTooltip" :tip="tooltipMessage" alignment="end" direction="bottom" />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";

// Props
interface Props {
  disabled?: boolean;
  tooltipMessage?: string;
  showTooltip?: boolean;
}

const { disabled = false, tooltipMessage = "", showTooltip = false } = defineProps<Props>();

const cssClasses = computed(() => [
  {
    disabled: disabled,
    "has-tooltip": showTooltip,
  },
]);
</script>

<style lang="scss">
@use "@styles/layout/breakpoint" as *;
@use "@styles/themes/sls-theme/tokens/colors" as *;
@use "@styles/themes/sls-theme/tokens/effects" as *;
@use "@styles/themes/sls-theme/tokens/spacing" as *;

.attached-toolbar {
  padding: $spacing-03;
  display: inline-flex;
  flex-flow: row;
  align-items: center;
  cursor: pointer;

  @include media("tablet-min") {
    padding: 0.75rem $spacing-03;
  }

  &:hover {
    background-color: $hover-ui;
  }

  &:focus {
    outline: 2px solid $focus;
  }

  &.disabled {
    cursor: not-allowed;

    &:active,
    &:focus {
      pointer-events: none;
    }

    &:hover {
      background: none;
    }

    .sls-icon {
      &:not([name*="Salis"]) {
        [fill] {
          fill: $disabled-02;
        }
      }
    }
  }

  &.has-tooltip {
    position: relative;

    .bx--tooltip__trigger {
      position: absolute;
      width: 100%;
      height: 100%;
      top: 0;
      left: 0;

      &:focus {
        &::before,
        .bx--assistive-text {
          display: none;
        }
      }

      &:hover {
        &::before,
        .bx--assistive-text {
          display: block;
        }
      }

      svg {
        display: none;
      }
    }
  }

  &:has(.space-indicator) {
    padding: $spacing-02 $spacing-03;
  }

  .sls-icon {
    &:not([name*="Salis"]) {
      [fill] {
        fill: $interactive-01;

        &[fill="#DFE8F9"] {
          opacity: 0.15;
        }
      }
    }
  }
}
</style>
